import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Drawer, Layout, Menu } from "antd";
import logo from "./tramango-logo-black.png";
import {
  BarChartOutlined,
  BellOutlined,
  DashboardOutlined,
  DollarOutlined,
  FileTextOutlined,
  GiftOutlined,
  HeartOutlined,
  IdcardOutlined,
  KeyOutlined,
  LineChartOutlined,
  LockOutlined,
  LogoutOutlined,
  NotificationOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SendOutlined,
  SettingOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { LogOut } from "../../features/auth/authThunk";
import { removeToken } from "../../utils/auth-helpers";
import { ROLE } from "../../utils/roles";
import { isDevEnv } from "../../utils/devEnv";
import { useLayout } from "../../context/layoutContext";
import BurgerIcon from "../burger/BurgerIcon";
import "./sidebar.scss";

const { Sider } = Layout;

const link = (to, label) => <Link to={to}>{label}</Link>;

const Sidebar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const { admin } = useSelector((state) => state.auth);
  const { isMobile, collapsed, mobileNavOpen, closeMobileNav } = useLayout();

  // Navigating from the mobile drawer should reveal the new page.
  useEffect(() => {
    closeMobileNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // The drawer's close icon follows mobileNavOpen two frames late, so on
  // opening it is first drawn as bars and then visibly morphs into an X.
  const [closeIconOpen, setCloseIconOpen] = useState(false);
  useEffect(() => {
    let second;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setCloseIconOpen(mobileNavOpen));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [mobileNavOpen]);

  const handleLogout = () => {
    dispatch(LogOut());
    removeToken();
  };

  const flightroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FLIGHT, ROLE.FINANCE];
  const insuranceroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FLIGHT, ROLE.INSURANCE];
  const financeroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FINANCE];
  const revenueroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FINANCE];
  const analyticsroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FINANCE, ROLE.MARKETING];
  const searchAnalyticsRoles = [ROLE.SUPER, ROLE.ADMIN, ROLE.MARKETING, ROLE.FINANCE];
  // SUPER only - see 149e024; admin account management is not for ROLE.ADMIN.
  const staffroles = [ROLE.SUPER];
  const userroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FLIGHT, ROLE.FINANCE, ROLE.MARKETING];
  const vaultroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FINANCE, ROLE.ENGINEER, ROLE.FLIGHT];
  const advertroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.MARKETING];
  const packagesroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.MARKETING];
  const discountroles = [ROLE.SUPER, ROLE.ADMIN, ROLE.FINANCE, ROLE.MARKETING];

  const can = (roles) => roles.includes(admin.admin_section_id);

  const sections = [
    {
      key: "main",
      label: "Main",
      children: [
        { key: "/", icon: <DashboardOutlined />, label: link("/", "Dashboard") },
        can(analyticsroles) && {
          key: "/analytics",
          icon: <LineChartOutlined />,
          label: link("/analytics", "Analytics and Marketing Data"),
        },
        can(searchAnalyticsRoles) && {
          key: "/search-analytics",
          icon: <SearchOutlined />,
          label: link("/search-analytics", "Search Analytics"),
        },
      ],
    },
    {
      key: "lists",
      label: "Lists",
      children: [
        can(userroles) && {
          key: "/tramango-users",
          icon: <TeamOutlined />,
          label: link("/tramango-users", "Tramango Users"),
        },
        can(discountroles) && {
          key: "/discount",
          icon: <TagsOutlined />,
          label: link("/discount", "Discounts"),
        },
        can(staffroles) && {
          key: "/staff",
          icon: <IdcardOutlined />,
          label: link("/staff", "Staffs"),
        },
        can(financeroles) && {
          key: "/finance",
          icon: <DollarOutlined />,
          label: link("/finance", "Finance"),
        },
        can(revenueroles) && {
          key: "/revenue",
          icon: <RiseOutlined />,
          label: link("/revenue", "Revenue"),
        },
        can(flightroles) && {
          key: "/flights",
          icon: <SendOutlined />,
          label: link("/flights", "Flights"),
        },
        can(insuranceroles) && {
          key: "/insurance",
          icon: <SafetyCertificateOutlined />,
          label: link("/insurance", "Insurance"),
        },
        can(advertroles) && {
          key: "/advert",
          icon: <NotificationOutlined />,
          label: link("/advert", "Advertisement"),
        },
        isDevEnv &&
          can(packagesroles) && {
            key: "/packages",
            icon: <GiftOutlined />,
            label: link("/packages", "Packages & Quick Trips"),
          },
        can(vaultroles) && {
          key: "/vault",
          icon: <LockOutlined />,
          label: link("/vault", "Tramango Vault"),
        },
      ],
    },
    {
      key: "useful",
      label: "Useful",
      children: [
        { key: "/stats", icon: <BarChartOutlined />, label: link("/stats", "Stats") },
        {
          key: "/notifications",
          icon: <BellOutlined />,
          label: link("/notifications", "Notifications"),
        },
      ],
    },
    {
      key: "service",
      label: "Service",
      children: [
        can(userroles) && {
          key: "/twofactor",
          icon: <KeyOutlined />,
          label: link("/twofactor", "2 Factor Authentication"),
        },
        {
          key: "/system-health",
          icon: <HeartOutlined />,
          label: link("/system-health", "System Health"),
        },
        { key: "/logs", icon: <FileTextOutlined />, label: link("/logs", "Logs") },
        { key: "/settings", icon: <SettingOutlined />, label: link("/settings", "Settings") },
      ],
    },
    {
      key: "user",
      label: "User",
      children: [
        { key: "/profile", icon: <UserOutlined />, label: link("/profile", "Profile") },
        {
          key: "logout",
          icon: <LogoutOutlined />,
          label: "Logout",
          onClick: handleLogout,
          className: "sidebar__logout",
        },
      ],
    },
  ];

  // On the icon rail, group titles have no room, so sections are separated
  // by dividers instead.
  const railMode = collapsed && !isMobile;
  const items = sections.flatMap((section, index) => {
    const children = section.children.filter(Boolean);
    if (children.length === 0) return [];
    if (railMode) {
      return index === 0 ? children : [{ type: "divider", key: `${section.key}-divider` }, ...children];
    }
    return [{ type: "group", key: section.key, label: section.label, children }];
  });

  const brand = (
    <Link to="/" className="sidebar__brand" aria-label="Tramango Super dashboard">
      {railMode ? (
        <span className="sidebar__mark">
          <img src={logo} alt="" />
        </span>
      ) : (
        <img className="sidebar__logo" src={logo} alt="Tramango" />
      )}
    </Link>
  );

  const menu = (
    <Menu
      className="sidebar__menu"
      mode="inline"
      inlineCollapsed={railMode}
      selectedKeys={[pathname]}
      items={items}
    />
  );

  if (isMobile) {
    return (
      <Drawer
        rootClassName="sidebar-drawer"
        placement="left"
        open={mobileNavOpen}
        onClose={closeMobileNav}
        width={288}
        closable={false}
        bodyStyle={{ padding: 0 }}
      >
        <nav className="sidebar sidebar--drawer" aria-label="Main navigation">
          <div className="sidebar__drawer-header">
            {brand}
            <button
              type="button"
              className="sidebar__close"
              onClick={closeMobileNav}
              aria-label="Close navigation"
            >
              <BurgerIcon open={closeIconOpen} />
            </button>
          </div>
          <div className="sidebar__scroll">{menu}</div>
        </nav>
      </Drawer>
    );
  }

  return (
    <Sider
      className="sidebar"
      theme="light"
      width={248}
      collapsedWidth={72}
      collapsed={railMode}
      trigger={null}
    >
      <nav className="sidebar__inner" aria-label="Main navigation">
        {brand}
        <div className="sidebar__scroll">{menu}</div>
      </nav>
    </Sider>
  );
};

export default Sidebar;
